import { FC, useEffect, useMemo, useRef, useState } from "react";
import { TextInput } from "react-native";
import { useFormContext } from "react-hook-form";

import VnAddressInput from "@/components/VnAddressInput";
import { VnCheckboxInput } from "@/components/VnCheckboxInput/VnCheckboxInput";
import { VnDateOfBirthInput } from "@/components/VnDateOfBirthInput";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import { useListHouseholdMemberRelationshipsBetweenMembersQuery } from "@/data/queries/householdMemberRelationshipsQueries";
import { useCurrentHouseholdMemberQuery } from "@/data/queries/householdMembersQueries";
import { useCurrentPatientQuery } from "@/data/queries/patientsQueries";
import {
  HouseholdMemberFormType,
  HouseholdMemberType,
} from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate";
import { getMfaPhoneNumber } from "@/services/firebase";
import { formatAddress, parseAddressComponents } from "@/utils/address";
import { phoneNumberMask, ssnMask } from "@/utils/string";

import { HouseholdMemberRelationshipForm } from "./HouseholdMemberRelationshipForm";
import { SecureTextEntryToggle } from "./SecureTextEntryToggle";

type ProfileInformationFormPropsType = {
  householdMember: HouseholdMemberType | undefined;
  isLoading: boolean | undefined;
  showIdentificationFields?: boolean;
};

export const ProfileInformationForm: FC<ProfileInformationFormPropsType> = ({
  householdMember,
  isLoading,
  showIdentificationFields = true,
}) => {
  const { t } = useTranslator();
  const { control, setValue, watch, reset } =
    useFormContext<HouseholdMemberFormType>();
  const mfaPhoneNumber = useMemo(() => getMfaPhoneNumber(), []);
  const [
    isHomeless,
    hasNoMailingAddress,
    shouldUseDifferentMailingAddress,
    mainAddressComponents,
    mailingAddressComponents,
    mainAddressStreetOne,
    mailingAddressStreetOne,
  ] = watch([
    "isHomeless",
    "hasNoMailingAddress",
    "shouldUseDifferentMailingAddress",
    "mainAddressComponents",
    "mailingAddressComponents",
    "mainAddressStreetOne",
    "mailingAddressStreetOne",
  ]);

  const { data: currentUserHouseholdMember } = useCurrentHouseholdMemberQuery();

  const isPrimaryUserHouseholdMember = Boolean(householdMember?.patientId);

  const { data: currentPatient } = useCurrentPatientQuery();

  const isEmailRequired =
    isPrimaryUserHouseholdMember && !currentPatient?.username;

  const shouldRenderRelationshipField =
    Boolean(currentUserHouseholdMember) && !isPrimaryUserHouseholdMember;

  const { data: householdMemberRelationships } =
    useListHouseholdMemberRelationshipsBetweenMembersQuery(
      {
        primaryHouseholdMemberId: currentUserHouseholdMember?.id ?? "",
        relationshipHouseholdMemberIds: householdMember
          ? [householdMember.id]
          : [],
      },
      {
        enabled:
          shouldRenderRelationshipField &&
          Boolean(currentUserHouseholdMember?.id) &&
          Boolean(householdMember?.id),
      },
    );

  const [isSsnVisible, setIsSsnVisible] = useState(false);

  const firstNameRef = useRef<TextInput>(null);
  const lastNameRef = useRef<TextInput>(null);
  const dobRef = useRef<{ focusMonth: () => void; focusYear: () => void }>(
    null,
  );
  const mainAddressRef = useRef<TextInput>(null);
  const phoneNumberRef = useRef<TextInput>(null);
  const emailRef = useRef<TextInput>(null);
  const ssnRef = useRef<TextInput>(null);
  const medicaidSubscriberIdRef = useRef<TextInput>(null);
  const medicaidCaseNumberRef = useRef<TextInput>(null);

  useEffect(() => {
    if (isHomeless && shouldUseDifferentMailingAddress) {
      setValue("shouldUseDifferentMailingAddress", false);
    }
    if (!isHomeless && hasNoMailingAddress) {
      setValue("hasNoMailingAddress", false);
    }
  }, [
    isHomeless,
    shouldUseDifferentMailingAddress,
    hasNoMailingAddress,
    setValue,
  ]);

  useEffect(() => {
    if (mainAddressComponents) {
      const parsed = parseAddressComponents(mainAddressComponents);
      setValue("mainAddressStreetOne", parsed.streetOne, {
        shouldValidate: true,
      });
      setValue("mainAddressStreetTwo", parsed.streetTwo, {
        shouldValidate: true,
      });
      setValue("mainAddressCity", parsed.city, { shouldValidate: true });
      setValue("mainAddressState", parsed.state, { shouldValidate: true });
      setValue("mainAddressZip", parsed.zip, { shouldValidate: true });
    }
  }, [mainAddressComponents, setValue]);

  useEffect(() => {
    if (mailingAddressComponents) {
      const parsed = parseAddressComponents(mailingAddressComponents);
      setValue("mailingAddressStreetOne", parsed.streetOne, {
        shouldValidate: true,
      });
      setValue("mailingAddressStreetTwo", parsed.streetTwo, {
        shouldValidate: true,
      });
      setValue("mailingAddressCity", parsed.city, { shouldValidate: true });
      setValue("mailingAddressState", parsed.state, { shouldValidate: true });
      setValue("mailingAddressZip", parsed.zip, { shouldValidate: true });
    }
  }, [mailingAddressComponents, setValue]);

  useEffect(() => {
    if (!householdMember || isLoading) return;

    const mainFormattedAddress = formatAddress({
      streetOne: householdMember.mainAddressStreetOne,
      streetTwo: householdMember.mainAddressStreetTwo,
      city: householdMember.mainAddressCity,
      state: householdMember.mainAddressState,
      zip: householdMember.mainAddressZip,
    });
    const mailingFormattedAddress = formatAddress({
      streetOne: householdMember.mailingAddressStreetOne,
      streetTwo: householdMember.mailingAddressStreetTwo,
      city: householdMember.mailingAddressCity,
      state: householdMember.mailingAddressState,
      zip: householdMember.mailingAddressZip,
    });
    const dateOfBirth = householdMember.dateOfBirth ?? undefined;

    const relationship = householdMemberRelationships?.[0];

    reset({
      ...householdMember,
      phoneNumber: householdMember.phoneNumber ?? mfaPhoneNumber ?? "",
      ssn: householdMember.ssn ?? "",
      dateOfBirth,
      shouldUseDifferentMailingAddress: Boolean(
        mainFormattedAddress !== mailingFormattedAddress ||
        householdMember.mainAddressComplement !==
          householdMember.mailingAddressComplement,
      ),
      hasNoMailingAddress: Boolean(householdMember.hasNoMailingAddress),
      mainFormattedAddress,
      mailingFormattedAddress,
      householdMemberRelationship: relationship
        ? {
            relationship: relationship.relationship,
            otherRelationship:
              relationship.relationship === "OTHER"
                ? (relationship.otherRelationship ?? "")
                : undefined,
          }
        : undefined,
    } as HouseholdMemberFormType);
  }, [
    householdMember,
    householdMemberRelationships,
    isLoading,
    mfaPhoneNumber,
    reset,
  ]);

  return (
    <>
      <VnTextInput
        autoFocus
        name="firstName"
        control={control}
        labelI18nKey="native:profileInformationScreen.firstName"
        textContentType="givenName"
        autoComplete="given-name"
        ref={firstNameRef}
        returnKeyType="next"
        onSubmitEditing={() => lastNameRef.current?.focus()}
      />
      <VnTextInput
        name="lastName"
        control={control}
        labelI18nKey="native:profileInformationScreen.lastName"
        textContentType="familyName"
        autoComplete="family-name"
        ref={lastNameRef}
        returnKeyType="next"
        onSubmitEditing={() => dobRef.current?.focusMonth()}
      />

      <VnDateOfBirthInput
        labelI18nKey="common:dateOfBirth"
        name="dateOfBirth"
        control={control}
        ref={dobRef}
        isRequired
        onYearSubmit={() => {
          if (!isHomeless) {
            mainAddressRef.current?.focus();
          } else {
            phoneNumberRef.current?.focus();
          }
        }}
      />

      {!isHomeless && (
        <>
          <VnAddressInput
            name="mainFormattedAddress"
            control={control}
            labelI18nKey="native:profileInformationScreen.homeAddress"
            ref={mainAddressRef}
            returnKeyType="next"
            onSubmitEditing={() => phoneNumberRef.current?.focus()}
            isAddressSelected={Boolean(mainAddressStreetOne)}
            onClear={() => {
              setValue("mainAddressComponents", undefined);
              setValue("mainFormattedAddress", "");
              setValue("mainAddressComplement", null);
              setValue("mainAddressStreetOne", "", { shouldValidate: true });
              setValue("mainAddressStreetTwo", "", { shouldValidate: true });
              setValue("mainAddressCity", "", { shouldValidate: true });
              setValue("mainAddressState", null, { shouldValidate: true });
              setValue("mainAddressZip", "", { shouldValidate: true });
            }}
            onPlaceSelected={(addressComponents, formattedAddress) => {
              setValue("mainAddressComponents", addressComponents);
              setValue("mainFormattedAddress", formattedAddress);
            }}
            onTextCleared={() => {
              setValue("mainAddressComponents", undefined);
              setValue("mainFormattedAddress", "");
              setValue("mainAddressComplement", null);
              setValue("mainAddressStreetOne", "", { shouldValidate: true });
              setValue("mainAddressStreetTwo", "", { shouldValidate: true });
              setValue("mainAddressCity", "", { shouldValidate: true });
              setValue("mainAddressState", null, { shouldValidate: true });
              setValue("mainAddressZip", "", { shouldValidate: true });
            }}
          />
          <VnTextInput
            name="mainAddressComplement"
            control={control}
            placeholderI18nKey="native:profileInformationScreen.addressComplement"
            isRequired={false}
          />
          <VnCheckboxInput
            name="shouldUseDifferentMailingAddress"
            control={control}
            isRequired={false}
            option={{
              label: t(
                "native:profileInformationScreen.useDifferentMailingAddress",
              ),
            }}
          />
          {shouldUseDifferentMailingAddress && (
            <>
              <VnAddressInput
                name="mailingFormattedAddress"
                control={control}
                labelI18nKey="native:profileInformationScreen.mailingAddress"
                returnKeyType="next"
                onSubmitEditing={() => phoneNumberRef.current?.focus()}
                isAddressSelected={Boolean(mailingAddressStreetOne)}
                onClear={() => {
                  setValue("mailingAddressComponents", undefined);
                  setValue("mailingFormattedAddress", "");
                  setValue("mailingAddressComplement", null);
                  setValue("mailingAddressStreetOne", "", {
                    shouldValidate: true,
                  });
                  setValue("mailingAddressStreetTwo", "", {
                    shouldValidate: true,
                  });
                  setValue("mailingAddressCity", "", { shouldValidate: true });
                  setValue("mailingAddressState", null, {
                    shouldValidate: true,
                  });
                  setValue("mailingAddressZip", "", { shouldValidate: true });
                }}
                onPlaceSelected={(addressComponents, formattedAddress) => {
                  setValue("mailingAddressComponents", addressComponents);
                  setValue("mailingFormattedAddress", formattedAddress);
                }}
                onTextCleared={() => {
                  setValue("mailingAddressComponents", undefined);
                  setValue("mailingFormattedAddress", "");
                  setValue("mailingAddressComplement", null);
                  setValue("mailingAddressStreetOne", "", {
                    shouldValidate: true,
                  });
                  setValue("mailingAddressStreetTwo", "", {
                    shouldValidate: true,
                  });
                  setValue("mailingAddressCity", "", { shouldValidate: true });
                  setValue("mailingAddressState", null, {
                    shouldValidate: true,
                  });
                  setValue("mailingAddressZip", "", { shouldValidate: true });
                }}
              />
              <VnTextInput
                name="mailingAddressComplement"
                control={control}
                placeholderI18nKey="native:profileInformationScreen.mailingAddressComplement"
                isRequired={false}
              />
            </>
          )}
        </>
      )}
      {isHomeless && !hasNoMailingAddress && (
        <>
          <VnAddressInput
            name="mailingFormattedAddress"
            control={control}
            labelI18nKey="native:profileInformationScreen.mailingAddress"
            returnKeyType="next"
            onSubmitEditing={() => phoneNumberRef.current?.focus()}
            isAddressSelected={Boolean(mailingAddressStreetOne)}
            onClear={() => {
              setValue("mailingAddressComponents", undefined);
              setValue("mailingFormattedAddress", "");
              setValue("mailingAddressComplement", null);
              setValue("mailingAddressStreetOne", "", {
                shouldValidate: true,
              });
              setValue("mailingAddressStreetTwo", "", {
                shouldValidate: true,
              });
              setValue("mailingAddressCity", "", { shouldValidate: true });
              setValue("mailingAddressState", null, {
                shouldValidate: true,
              });
              setValue("mailingAddressZip", "", { shouldValidate: true });
            }}
            onPlaceSelected={(addressComponents, formattedAddress) => {
              setValue("mailingAddressComponents", addressComponents);
              setValue("mailingFormattedAddress", formattedAddress);
            }}
            onTextCleared={() => {
              setValue("mailingAddressComponents", undefined);
              setValue("mailingFormattedAddress", "");
              setValue("mailingAddressComplement", null);
              setValue("mailingAddressStreetOne", "", {
                shouldValidate: true,
              });
              setValue("mailingAddressStreetTwo", "", {
                shouldValidate: true,
              });
              setValue("mailingAddressCity", "", { shouldValidate: true });
              setValue("mailingAddressState", null, {
                shouldValidate: true,
              });
              setValue("mailingAddressZip", "", { shouldValidate: true });
            }}
          />
          <VnTextInput
            name="mailingAddressComplement"
            control={control}
            placeholderI18nKey="native:profileInformationScreen.mailingAddressComplement"
            isRequired={false}
          />
        </>
      )}
      <VnCheckboxInput
        name="isHomeless"
        control={control}
        isRequired={false}
        option={{
          label: t("native:profileInformationScreen.noHomeAddress"),
        }}
      />
      {isHomeless && (
        <VnCheckboxInput
          name="hasNoMailingAddress"
          control={control}
          isRequired={false}
          option={{
            label: t("native:profileInformationScreen.noMailingAddress"),
          }}
        />
      )}
      <VnTextInput
        name="phoneNumber"
        control={control}
        isRequired={isPrimaryUserHouseholdMember}
        labelI18nKey="native:profileInformationScreen.phoneNumber"
        mask={{
          maskGenerator: phoneNumberMask,
        }}
        ref={phoneNumberRef}
        returnKeyType="next"
        onSubmitEditing={() => emailRef.current?.focus()}
      />
      <VnTextInput
        name="email"
        control={control}
        isRequired={isEmailRequired}
        labelI18nKey="native:profileInformationScreen.email"
        ref={emailRef}
        returnKeyType={showIdentificationFields ? "next" : "done"}
        onSubmitEditing={() => {
          if (showIdentificationFields) {
            ssnRef.current?.focus();
          }
        }}
      />
      {showIdentificationFields && (
        <>
          <VnTextInput
            name="ssn"
            control={control}
            labelI18nKey="native:profileInformationScreen.socialSecurityNumber"
            isRequired={false}
            mask={{
              maskGenerator: ssnMask,
            }}
            ref={ssnRef}
            returnKeyType="next"
            onSubmitEditing={() => medicaidSubscriberIdRef.current?.focus()}
            secureTextEntry={!isSsnVisible}
            keyboardType="numeric"
            isPasswordField={false}
            rightElement={
              <SecureTextEntryToggle
                isVisible={isSsnVisible}
                onPress={() => setIsSsnVisible(prev => !prev)}
              />
            }
          />
          <VnTextInput
            name="medicaidSubscriberId"
            control={control}
            labelI18nKey="native:profileInformationScreen.medicaidSubscriberId"
            isRequired={false}
            ref={medicaidSubscriberIdRef}
            returnKeyType="next"
            onSubmitEditing={() => medicaidCaseNumberRef.current?.focus()}
          />
          <VnTextInput
            name="medicaidCaseNumber"
            control={control}
            labelI18nKey="native:profileInformationScreen.medicaidCaseNumber"
            isRequired={false}
            ref={medicaidCaseNumberRef}
            returnKeyType="done"
          />
        </>
      )}
      {shouldRenderRelationshipField && <HouseholdMemberRelationshipForm />}
    </>
  );
};
